<template>
  <div class="productos-page">
    <div class="bg-grid" aria-hidden="true"></div>

    <div class="productos-shell">
      <!-- Header -->
      <header class="page-header fade-in">
        <div class="page-header-left">
          <div class="page-icon-box">
            <v-icon color="#F2F2ED" size="20"
              >mdi-package-variant-closed</v-icon
            >
          </div>
          <div>
            <h1 class="page-title">Productos</h1>
            <p class="page-sub">Gestiona el inventario y realiza ventas</p>
          </div>
        </div>

        <button
          class="icon-btn-cart"
          @click="mostrarVenta = !mostrarVenta"
          title="Carrito"
        >
          <v-icon size="20" :color="mostrarVenta ? '#C8FF4D' : '#F2F2ED'"
            >mdi-cart</v-icon
          >
          <span v-if="cartCount > 0" class="cart-badge">{{ cartCount }}</span>
        </button>
      </header>

      <!-- Búsqueda -->
      <section class="fade-in delay-100">
        <div class="search-input-wrapper">
          <v-icon size="18" color="#5C5C62">mdi-magnify</v-icon>
          <input
            v-model="busqueda"
            type="text"
            class="search-input"
            placeholder="Buscar producto…"
          />
          <button v-if="busqueda" class="clear-btn" @click="busqueda = ''">
            <v-icon size="14" color="#5C5C62">mdi-close</v-icon>
          </button>
        </div>
      </section>

      <!-- Tabla de productos -->
      <section class="fade-in delay-200 mt-lg">
        <div class="card-flat card-flat-nopad">
          <div class="table-head">
            <span class="section-eyebrow">
              <span class="tick-mark"></span>
              Inventario de productos
            </span>
          </div>

          <v-data-table
            :headers="encabezadoTabla"
            :items="productos"
            :search="busqueda"
            no-data-text="No hay productos registrados"
            no-results-text="No se encontraron productos con esa búsqueda"
            sort-by="nombre"
            show-expand
            class="dash-table"
            :footer-props="{ itemsPerPageText: 'Por página' }"
          >
            <template v-slot:[`item.imagen`]="{ item }">
              <div
                class="product-image-wrapper"
                @click="verImagen(item.imagen)"
              >
                <img
                  :src="urlImagen(item.imagen)"
                  alt="Producto"
                  class="product-image-table"
                />
              </div>
            </template>

            <template v-slot:[`item.nombre`]="{ item }">
              <span class="cell-strong">{{ item.nombre }}</span>
            </template>

            <template v-slot:[`item.precio`]="{ item }">
              <span class="cell-price"
                >${{ formatearPrecio(item.precio) }}</span
              >
            </template>

            <template v-slot:[`item.categoria`]="{ item }">
              <span class="status-tag status-tag-neutral">{{
                item.categoria
              }}</span>
            </template>

            <template v-slot:[`item.stock`]="{ item }">
              <span
                :class="[
                  'status-tag',
                  item.stock < 10 ? 'status-tag-error' : 'status-tag-success'
                ]"
              >
                {{ item.stock }} unidades
              </span>
            </template>

            <template v-slot:item.acciones="{ item }">
              <div class="flex gap-xs">
                <button
                  v-if="puedeVer('productos.vender')"
                  class="icon-btn icon-btn-accent"
                  :disabled="item.stock <= 0"
                  @click="agregarAlCarrito(item)"
                  title="Agregar al carrito"
                >
                  <v-icon size="15" color="#C8FF4D">mdi-cart-plus</v-icon>
                </button>
                <button
                  v-if="puedeVer('productos.editar')"
                  class="icon-btn"
                  @click="editar(item)"
                  title="Editar"
                >
                  <v-icon size="15" color="#8C8C93">mdi-pencil</v-icon>
                </button>
                <button
                  v-if="puedeVer('productos.eliminar')"
                  class="icon-btn icon-btn-danger"
                  @click="eliminar(item)"
                  title="Eliminar"
                >
                  <v-icon size="15" color="#FF6B6B">mdi-delete</v-icon>
                </button>
              </div>
            </template>

            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="expanded-content">
                <div class="expanded-inner">
                  <span class="expanded-label"
                    >Descripción de {{ item.nombre }}</span
                  >
                  <p class="expanded-text">
                    {{ item.descripcion || "Sin descripción" }}
                  </p>
                </div>
              </td>
            </template>

            <template slot="no-data">
              <div class="empty-state">
                <div class="empty-icon">
                  <v-icon size="28" color="#5C5C62"
                    >mdi-package-variant-closed</v-icon
                  >
                </div>
                <h3 class="empty-title">No hay productos registrados</h3>
                <p class="empty-desc">Comienza agregando el primer producto</p>
                <button
                  v-if="puedeVer('productos.crear')"
                  class="btn-primary mt-lg"
                  @click="mostrarDialogo = true"
                >
                  <v-icon size="15" class="mr-2">mdi-plus</v-icon>
                  Agregar producto
                </button>
              </div>
            </template>

            <template v-slot:loading>
              <div class="loading-state">
                <div class="spinner"></div>
                <p class="loading-text">Cargando productos…</p>
              </div>
            </template>
          </v-data-table>
        </div>
      </section>

      <!-- Backdrop -->
      <div
        v-if="mostrarVenta"
        class="cart-backdrop"
        @click="mostrarVenta = false"
      ></div>

      <!-- Carrito de Compras -->
      <div class="cart-drawer" :class="{ 'cart-drawer-open': mostrarVenta }">
        <div class="cart-panel">
          <div class="cart-head">
            <span class="section-eyebrow">
              <span class="tick-mark"></span>
              Carrito de compra
            </span>
            <button class="icon-btn" @click="mostrarVenta = false">
              <v-icon size="16" color="#8C8C93">mdi-close</v-icon>
            </button>
          </div>

          <div class="cart-body">
            <venta-carrito
              ref="carrito"
              :productos-disponibles="productosDisponibles"
              :mostrar-buscador-producto="false"
              @mensaje="mostrarSnackbar"
              @venta-registrada="onVentaRegistrada"
              @actualizado="onCarritoActualizado"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- FAB -->
    <button
      v-if="puedeVer('productos.crear') && !mostrarVenta"
      class="fab-button"
      @click="mostrarDialogo = true"
    >
      <v-icon size="22" color="#0E0E10">mdi-plus</v-icon>
    </button>

    <!-- Dialog: Ver Imagen -->
    <v-dialog v-model="dialogoImagen" max-width="700px">
      <div class="modal-panel">
        <div class="modal-body modal-body-nopad">
          <img
            :src="imagenSeleccionada"
            alt="Producto"
            class="image-preview-full"
          />
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="dialogoImagen = false">
            Cerrar
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Dialog: Formulario Producto -->
    <v-dialog v-model="mostrarDialogo" persistent max-width="600px">
      <form-producto
        @cerrado="onCerrado"
        @registrar="onRegistrar"
        :producto="producto"
        :titulo="titulo"
      />
    </v-dialog>

    <!-- Dialog: Eliminar Producto -->
    <v-dialog v-model="mostrarDialogoEliminar" max-width="480px">
      <div class="modal-panel">
        <div class="modal-head">
          <h3 class="modal-title">Confirmar eliminación</h3>
        </div>

        <div class="modal-body">
          <p class="modal-text">
            ¿Estás seguro de eliminar el producto
            <strong class="text-error">"{{ itemSeleccionado }}"</strong>?
          </p>
          <div class="alert-box">
            <v-icon size="18" color="#FFB84D">mdi-alert</v-icon>
            <div class="alert-text">
              Esta acción no se puede deshacer y se eliminará del inventario.
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-ghost" @click="cerrarDialogoEliminar">
            Cancelar
          </button>
          <button
            class="btn-danger-solid"
            @click="confirmarEliminar"
            :disabled="cargando"
          >
            <span v-if="!cargando">Eliminar</span>
            <span v-else class="loading-inline"
              ><span class="spinner-sm"></span>Eliminando</span
            >
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Loading Overlay -->
    <div v-if="cargando && productos.length === 0" class="loading-overlay">
      <div class="loading-inner">
        <div class="spinner"></div>
        <p class="loading-text">Cargando productos…</p>
      </div>
    </div>

    <!-- Snackbar -->
    <v-snackbar
      v-model="mostrarMensaje"
      :timeout="3000"
      :color="mensaje.color"
      top
      right
    >
      <div class="flex items-center gap-md">
        <v-icon color="white">{{ mensajeIcon }}</v-icon>
        <span>{{ mensaje.texto }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon small @click="mostrarMensaje = false" v-bind="attrs">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import FormProducto from "./FormProducto.vue";
import VentaCarrito from "./VentaCarrito.vue";
import DialogoEliminar from "../Dialogos/DialogoEliminar.vue";
import HttpService from "../../Servicios/HttpService";
import Utiles from "../../Servicios/Utiles";

export default {
  name: "Productos",
  components: { FormProducto, VentaCarrito, DialogoEliminar },

  data: () => ({
    mostrarVenta: false,
    cartCount: 0,
    dialogoImagen: false,
    imagenSeleccionada: "",
    busqueda: "",
    cargando: false,
    matriculaSeleccionada: "",
    mostrarDialogo: false,
    mostrarDialogoEliminar: false,
    producto: {
      nombre: "",
      descripcion: "",
      precio: "",
      stock: "",
      categoria: "",
      imagen: ""
    },
    productos: [],
    mensaje: {
      texto: "",
      color: ""
    },
    mostrarMensaje: false,
    encabezadoTabla: [
      {
        text: "Imagen",
        align: "start",
        sortable: false,
        value: "imagen"
      },
      { text: "Nombre", value: "nombre" },
      { text: "Precio", value: "precio" },
      { text: "Categoría", value: "categoria" },
      { text: "Inventario", value: "stock" },
      { text: "Acciones", value: "acciones", sortable: false }
    ],
    titulo: "Agregar",
    itemSeleccionado: ""
  }),

  computed: {
    mensajeIcon() {
      switch (this.mensaje.color) {
        case "success":
          return "mdi-check-circle";
        case "error":
          return "mdi-alert-circle";
        case "warning":
          return "mdi-alert";
        default:
          return "mdi-information";
      }
    },

    // Productos con stock, para pasarle al carrito (respeta el tope de
    // cantidad que puede agregarse por producto).
    productosDisponibles() {
      return this.productos.filter(p => p.stock > 0);
    }
  },

  mounted() {
    this.obtenerProductos();
  },

  methods: {
    puedeVer(permiso) {
      return this.$store.getters.tienePermiso(permiso);
    },

    formatearPrecio(valor) {
      const numero = Number(valor);
      return isNaN(numero) ? "0.00" : numero.toFixed(2);
    },

    mostrarSnackbar(payload) {
      this.mostrarMensaje = true;
      this.mensaje = payload;
    },

    verImagen(imagen) {
      this.imagenSeleccionada = this.urlImagen(imagen);
      this.dialogoImagen = true;
    },

    urlImagen(imagen) {
      return Utiles.generarURL(imagen);
    },

    editar(producto) {
      this.titulo = "Editar";
      this.producto = { ...producto, imagenActual: producto.imagen };
      this.mostrarDialogo = true;
    },

    eliminar(producto) {
      this.itemSeleccionado = producto.nombre;
      this.producto = producto;
      this.mostrarDialogoEliminar = true;
    },

    cerrarDialogoEliminar() {
      this.mostrarDialogoEliminar = false;
      this.producto = {
        nombre: "",
        descripcion: "",
        precio: "",
        stock: "",
        categoria: "",
        imagen: ""
      };
    },

    async confirmarEliminar() {
      this.cargando = true;
      let payload = {
        metodo: "delete",
        id: this.producto.id
      };

      try {
        const respuesta = await HttpService.eliminar("productos.php", payload);

        if (respuesta.error) {
          this.mostrarMensaje = true;
          this.mensaje = {
            texto: respuesta.mensaje,
            color: "error"
          };
        } else {
          this.mostrarDialogoEliminar = false;
          this.mostrarMensaje = true;
          this.mensaje = {
            texto: "✅ Producto eliminado",
            color: "success"
          };
          await this.obtenerProductos();
        }
      } catch (error) {
        this.mostrarMensaje = true;
        this.mensaje = {
          texto: "❌ Error al eliminar el producto",
          color: "error"
        };
      } finally {
        this.cargando = false;
      }
    },

    onCerrado(valor) {
      this.producto = {
        nombre: "",
        descripcion: "",
        precio: "",
        stock: "",
        categoria: "",
        imagen: ""
      };
      this.mostrarDialogo = valor;
      this.titulo = "Agregar";
    },

    async onRegistrar(producto) {
      let metodo = this.titulo === "Agregar" ? "post" : "put";
      this.producto = producto;
      this.cargando = true;

      let payload = {
        metodo: metodo,
        producto: this.producto
      };

      try {
        const respuesta = await HttpService.registrar(payload, "productos.php");

        if (respuesta) {
          this.producto = {
            nombre: "",
            descripcion: "",
            precio: "",
            stock: "",
            categoria: "",
            imagen: ""
          };
          this.mostrarDialogo = false;
          this.mostrarMensaje = true;
          this.mensaje = {
            texto: "✅ Producto registrado correctamente",
            color: "success"
          };
          await this.obtenerProductos();
          this.titulo = "Agregar";
        }
      } catch (error) {
        this.mostrarMensaje = true;
        this.mensaje = {
          texto: "❌ Error al registrar el producto",
          color: "error"
        };
      } finally {
        this.cargando = false;
      }
    },

    async obtenerProductos() {
      this.cargando = true;
      let payload = { metodo: "get" };

      try {
        const respuesta = await HttpService.obtenerConDatos(
          payload,
          "productos.php"
        );
        this.productos = respuesta;
      } catch (error) {
        this.mostrarMensaje = true;
        this.mensaje = {
          texto: "❌ Error al cargar productos",
          color: "error"
        };
      } finally {
        this.cargando = false;
      }
    },

    agregarAlCarrito(producto) {
      this.mostrarVenta = true;
      this.$refs.carrito.agregarProducto(producto);
    },

    onCarritoActualizado({ cantidad }) {
      this.cartCount = cantidad;
    },

    async onVentaRegistrada() {
      // El stock cambió, refrescamos el inventario.
      await this.obtenerProductos();
      this.mostrarVenta = false;
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap");

/* ===== TOKENS ===== */



.productos-page {
  --bg: #0e0e10;
  --card: #17171a;
  --card-border: #26262a;
  --text-primary: #f2f2ed;
  --text-secondary: #8c8c93;
  --text-tertiary: #5c5c62;
  --accent: #c8ff4d;
  --accent-dim: rgba(200, 255, 77, 0.12);
  --warning: #ffb84d;
  --error: #ff6b6b;
  --radius: 4px;

  --font-display: "Space Grotesk", sans-serif;
  --font-body: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: "JetBrains Mono", monospace;
}

.productos-page {
  min-height: 100vh;
  background: var(--bg);
  color: var(--text-primary);
  font-family: var(--font-body);
  position: relative;
  padding: 40px;
}

.bg-grid {
  position: fixed;
  inset: 0;
  background-image: linear-gradient(
      rgba(242, 242, 237, 0.02) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(242, 242, 237, 0.02) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
  z-index: 0;
}

.productos-shell {
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
}

/* ===== HEADER ===== */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 22px 26px;
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  margin-bottom: 20px;
}

.page-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-icon-box {
  width: 42px;
  height: 42px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-title {
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 2px;
}

.page-sub {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.cart-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  margin-left: 8px;
  border-radius: 999px;
  background: rgba(14, 14, 16, 0.25);
  font-size: 10.5px;
  font-weight: 700;
}

/* ===== SEARCH ===== */
.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 0 14px;
  transition: border-color 0.15s ease;
}

.search-input-wrapper:focus-within {
  border-color: var(--accent);
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  padding: 12px 0;
  color: var(--text-primary);
  font-size: 13.5px;
  font-family: var(--font-body);
}

.search-input::placeholder {
  color: var(--text-tertiary);
}

.clear-btn {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 4px;
}

/* ===== CARD / TABLA ===== */
.card-flat {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
}

.card-flat-nopad {
  overflow: hidden;
}

.table-head {
  padding: 18px 22px;
  border-bottom: 1px solid var(--card-border);
}

.section-eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.tick-mark {
  width: 12px;
  height: 1px;
  background: var(--accent);
}

.product-image-wrapper {
  width: 44px;
  height: 44px;
  border-radius: var(--radius);
  overflow: hidden;
  cursor: pointer;
  border: 1px solid var(--card-border);
  transition: border-color 0.15s ease;
}

.product-image-wrapper:hover {
  border-color: var(--accent);
}

.product-image-table {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cell-strong {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.cell-price {
  font-size: 13px;
  font-weight: 600;
  color: var(--accent);
}

.icon-btn {
  width: 28px;
  height: 28px;
  border-radius: var(--radius);
  background: transparent;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.icon-btn:hover:not(:disabled) {
  border-color: var(--card-border);
  background: rgba(255, 255, 255, 0.03);
}

.icon-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.icon-btn-accent:hover:not(:disabled) {
  border-color: rgba(200, 255, 77, 0.3);
  background: var(--accent-dim);
}

.icon-btn-danger:hover {
  border-color: rgba(255, 107, 107, 0.3);
  background: rgba(255, 107, 107, 0.06);
}

/* Fila expandida */
.expanded-content {
  background: rgba(255, 255, 255, 0.015) !important;
}

.expanded-inner {
  padding: 16px 22px;
}

.expanded-label {
  font-family: var(--font-mono);
  font-size: 10.5px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}

.expanded-text {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 6px 0 0;
  line-height: 1.6;
}

/* ===== STATUS TAG ===== */
.status-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: var(--radius);
  font-size: 10.5px;
  font-weight: 600;
  font-family: var(--font-mono);
  white-space: nowrap;
}

.status-tag-success {
  background: var(--accent-dim);
  color: var(--accent);
}

.status-tag-error {
  background: rgba(255, 107, 107, 0.12);
  color: var(--error);
}

.status-tag-neutral {
  background: rgba(140, 140, 147, 0.14);
  color: var(--text-secondary);
}

/* ===== EMPTY / LOADING ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 56px 20px;
}

.empty-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--radius);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-title {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 6px;
}

.empty-desc {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 0;
  gap: 16px;
}

.loading-text {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-secondary);
}

/* ===== CARRITO ===== */

.cart-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 22px;
  border-bottom: 1px solid var(--card-border);
}

.cart-body {
  padding: 22px;
}

/* ===== BUTTONS ===== */
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  color: var(--bg);
  border: none;
  border-radius: var(--radius);
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-primary:hover:not(:disabled) {
  filter: brightness(1.08);
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.btn-ghost:hover {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

.btn-danger-solid {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--error);
  color: #fa0e0e;
  border: none;
  border-radius: var(--radius);
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-danger-solid:hover:not(:disabled) {
  filter: brightness(1.1);
}

.btn-danger-solid:disabled {
  opacity: 0.6;
  cursor: default;
}

.loading-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.spinner {
  width: 22px;
  height: 22px;
  border: 2px solid var(--card-border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.spinner-sm {
  width: 11px;
  height: 11px;
  border: 2px solid rgba(0, 0, 0, 0.25);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ===== FAB ===== */
.fab-button {
  position: fixed;
  bottom: 32px;
  right: 32px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--accent);
  border: none;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  cursor: pointer;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, filter 0.15s ease;
}

.fab-button:hover {
  transform: scale(1.06);
  filter: brightness(1.05);
}

/* ===== MODAL ===== */
.modal-panel {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
}

.modal-head {
  padding: 22px 24px;
  border-bottom: 1px solid var(--card-border);
}

.modal-title {
  font-family: var(--font-display);
  font-size: 17px;
  font-weight: 700;
  margin: 0;
}

.modal-body {
  padding: 24px;
}

.modal-body-nopad {
  padding: 0;
}

.image-preview-full {
  width: 100%;
  display: block;
}

.modal-text {
  font-size: 13.5px;
  color: var(--text-secondary);
  margin: 0 0 18px;
  line-height: 1.6;
}

.modal-text strong {
  color: var(--error);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--card-border);
}

.alert-box {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: rgba(255, 184, 77, 0.08);
  border: 1px solid rgba(255, 184, 77, 0.25);
  border-radius: var(--radius);
  padding: 12px 14px;
}

.alert-text {
  font-size: 12.5px;
  color: var(--text-secondary);
  line-height: 1.5;
}

/* ===== LOADING OVERLAY ===== */
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(14, 14, 16, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
}

.loading-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

/* ===== MOTION ===== */
.fade-in {
  animation: fadeIn 0.4s ease both;
}

.delay-100 {
  animation-delay: 0.05s;
}
.delay-200 {
  animation-delay: 0.1s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .fade-in,
  .spinner,
  .spinner-sm {
    animation: none;
  }
}

/* ===== Overrides v-data-table (Vuetify) ===== */
.dash-table {
  background: transparent !important;
}

.dash-table >>> .v-data-table__wrapper {
  background: transparent;
}

.dash-table >>> table {
  background: transparent;
}

.dash-table >>> thead th {
  background: rgba(255, 255, 255, 0.02) !important;
  color: var(--text-tertiary) !important;
  text-transform: uppercase;
  font-size: 10px !important;
  letter-spacing: 0.08em;
  font-family: var(--font-mono);
  border-bottom: 1px solid var(--card-border) !important;
  padding: 0 22px !important;
}

.dash-table >>> tbody td {
  border-bottom: 1px solid var(--card-border) !important;
  padding: 10px 22px !important;
}

.dash-table >>> tbody tr:hover {
  background: rgba(255, 255, 255, 0.02) !important;
}

/* Fila de "sin resultados" al buscar (no-results-text) */
.dash-table >>> tbody tr:hover:has(td[colspan]) {
  background: transparent !important;
}

.dash-table >>> tbody td[colspan] {
  text-align: center;
  color: var(--text-secondary) !important;
  font-size: 13px !important;
  padding: 40px 22px !important;
}

.dash-table >>> .v-data-footer {
  color: var(--text-tertiary) !important;
  border-top: 1px solid var(--card-border) !important;
  font-size: 12px;
  padding: 0 22px;
}

.dash-table >>> .v-data-footer__select .v-select__selection {
  color: var(--text-secondary) !important;
}

.dash-table >>> .v-data-footer__icons-before .v-icon,
.dash-table >>> .v-data-footer__icons-after .v-icon {
  color: var(--text-secondary) !important;
}

.dash-table >>> .v-icon.mdi-chevron-down {
  color: var(--text-tertiary) !important;
}

/* ===== ÍCONO CARRITO EN HEADER ===== */
.icon-btn-cart {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.icon-btn-cart:hover {
  border-color: var(--accent);
  background: var(--accent-dim);
}

.cart-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--accent);
  color: var(--bg);
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-mono);
}

/* ===== DRAWER CARRITO ===== */
/* ===== DRAWER CARRITO (custom, sin Vuetify) ===== */
.cart-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 998;
}

.cart-drawer {
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 560px;
  max-width: 100vw;
  background: var(--bg);
  z-index: 999;
  transform: translateX(100%);
  transition: transform 0.25s ease;
}

.cart-drawer-open {
  transform: translateX(0);
}

.cart-drawer .cart-panel {
  background: var(--card);
  border-left: 1px solid var(--accent);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.cart-drawer .cart-body {
  flex: 1;
  overflow-y: auto;
  padding: 22px;
}

@media (max-width: 480px) {
  .cart-drawer {
    width: 100%;
  }
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .productos-page {
    padding: 20px;
  }

  .page-header {
    padding: 18px;
  }

  .dash-table >>> thead th,
  .dash-table >>> tbody td {
    padding: 10px 14px !important;
    font-size: 12px !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Productos/ProductosComponente.vue